import { Link, useMatch, useParams } from 'react-router-dom-v5-compat'

import type { ApolloError } from '@apollo/client'

import {
  Button,
  CriticalIcon,
  EmptyState,
  LockIcon,
  Text,
} from '@buffer-mono/popcorn'

import { useChannels } from '~publish/components/PublishSidebar/useChannels'
import * as routes from '~publish/legacy/routes'

export function ChannelError({ error }: { error: ApolloError }): JSX.Element {
  const { channels } = useChannels()
  const hasOnlyOneChannel = channels.length === 1
  const channelId = channels?.[0]?.id
  const isNewCalendarSingleChannel = useMatch(
    routes.newCalendarSingleChannel.route,
  )
  const params = useParams<{ granularity: 'week' | 'month' }>()

  let destinationGoBackUrl = hasOnlyOneChannel
    ? routes.channel.getRoute(channelId)
    : routes.allChannels.getRoute()

  if (isNewCalendarSingleChannel) {
    destinationGoBackUrl = hasOnlyOneChannel
      ? routes.newCalendarSingleChannel.getRoute(
          channelId,
          params.granularity ?? 'week',
        )
      : routes.newCalendarAllChannels.getRoute(params.granularity ?? 'week')
  }

  if (isPermissionError(error)) {
    return (
      <EmptyState size="xlarge" variant="neutral">
        <EmptyState.Icon>
          <LockIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Channel unavailable</EmptyState.Heading>
        <EmptyState.Description>
          You don’t have access to view this channel
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button as={Link} to={destinationGoBackUrl} size="large">
            Go back to {hasOnlyOneChannel ? 'Channel' : 'All Channels'}
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    )
  }
  if (isNotFoundError(error)) {
    return (
      <EmptyState size="xlarge" variant="neutral">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Channel not found</EmptyState.Heading>
        <EmptyState.Description>
          This channel does not exist
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button as={Link} to={destinationGoBackUrl} size="large">
            Go back to {hasOnlyOneChannel ? 'Channel' : 'All Channels'}
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    )
  }
  return (
    <EmptyState size="xlarge" variant="critical">
      <EmptyState.Icon>
        <CriticalIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Failed to load</EmptyState.Heading>
      <EmptyState.Description>
        Error happened, please let our team know about it.{' '}
        <Text color="critical">{error.message}</Text>
      </EmptyState.Description>
    </EmptyState>
  )
}

// TODO This error codes should be improved on the backend so is less error prone
function isPermissionError(error: ApolloError): boolean {
  return (
    error.message.startsWith('Failed to resolve hasActiveMemberDevice') ||
    // When the user does not have permissions on the channel
    error.message.includes("You don't have access to this channel")
  )
}

function isNotFoundError(error: ApolloError): boolean {
  return (
    error.message.startsWith('Bad Request') || // When the channel ID is malformed
    error.message.includes('Actor can not access this organization') || // When the channel ID is from another organization
    error.message.includes('Channel not found') // When the channel ID is not found in the database
  )
}
